import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LazyImage } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    imageSize = "imageSize",
    imageVerticalAlignment = "imageVerticalAlignment",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    overlayPositioning = "overlayPositioning",
    disableButton = "disableButton",
    contentPadding = "contentPadding",
    centerTextVertically = "centerTextVertically",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    headingLeft = "headingLeft",
    subHeadingLeft = "subHeadingLeft",
    headingRight = "headingRight",
    subHeadingRight = "subHeadingRight",
    headingTop = "headingTop",
    subHeadingTop = "subHeadingTop",
    headingBottom = "headingBottom",
    subHeadingBottom = "subHeadingBottom",
    btnMinWidth = "btnMinWidth",
    btnBorderWitdh = "btnBorderWidth",
    btnPadding = "btnPadding",
    btnMarginTop = "btnMarginTop",
    btnMarginBottom = "btnMarginBottom",
    btnMarginLeft = "btnMarginLeft",
    btnMarginRight = "btnMarginRight",
    btnBorderLine = "btnBorderLine",
    headingPosition = "headingPosition",
    subHeadingPosition = "subHeadingPosition",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.imageSize]: string;
        [fields.imageVerticalAlignment]:
            | "top"
            | "center"
            | "bottom";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.headingLeft]: number;
        [fields.headingRight]: number;
        [fields.headingTop]: number;
        [fields.headingBottom]: number;
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.subheading]: string;
        [fields.subHeadingLeft]: number;
        [fields.subHeadingRight]: number;
        [fields.subHeadingTop]: number;
        [fields.subHeadingBottom]: number;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imageOverlay]: string;
        [fields.overlayPositioning]: "top" | "center" | "bottom";
        [fields.disableButton]: boolean;
        [fields.contentPadding]: number;
        [fields.centerTextVertically]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.customCSS]: string;
        [fields.btnMarginBottom]: number;
        [fields.btnMarginLeft]: number;
        [fields.btnMarginTop]: number;
        [fields.btnMarginRight]: number;
        [fields.btnPadding]: number;
        [fields.btnBorderWitdh]: number;
        [fields.btnMinWidth]: number;
        [fields.btnBorderLine]: string;
        [fields.headingPosition]: string;
        [fields.subHeadingPosition]: string;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    overlayHeadingPadding?: InjectableCss;
    overlaySubHeadingPadding?: InjectableCss;
    bannerButtonStyle?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    imageWrapper?: InjectableCss;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            position: relative;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            overflow: hidden;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    overlayWrapper: {
        css: css`
            width: auto;
            color: white;
            text-align: center;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            position: absolute;
                            height: 100%;
                            display: flex;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            img {
                display: block;
                width: 100%;
                height: auto;
            }
        `,
    },
};

export const Banner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const onClick = () => {
        // GE-180 base bug fix for external URLs
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            window.location.href = url;
        } else {
            history.push(url);
        }
        // GE-180
    };
    const backgroundStyles =
        fields.background === "image"
            ? null
            : `background-color: ${fields.backgroundColor};`;

    let imageAlignmentStyle;
    switch (fields.imageVerticalAlignment) {
        case "top":
            imageAlignmentStyle = "justify-content: start;";
            break;
        case "center":
            imageAlignmentStyle = "justify-content: center;";
            break;
        case "bottom":
            imageAlignmentStyle = "justify-content: end;";
            break;
    }

    let minimumHeightStyles;
    switch (fields.minimumHeight) {
        case "1/4 viewport":
            minimumHeightStyles = "height: 25vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "height: 50vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "height: 100vh;";
            break;
    }

    let overlayPositioningStyles;
    if (fields.overlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.overlayPositioning === "center") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.overlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    let fontSizeStyles;
    if (fields.responsiveFontSizes || fields.customFontSizes) {
        fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${imageAlignmentStyle}
            ${minimumHeightStyles}
            ${fontSizeStyles}
        `,
    };

    const overlayParentStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            ${overlayPositioningStyles}
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            padding: ${fields.contentPadding}px;
            ${
                fields.centerTextVertically &&
                `
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;`
            }
        `,
    };

    const heading = {
        css: css`
            bottom: ${fields.headingBottom};
            left: ${fields.headingLeft}px;
            top: ${fields.headingTop}px;
            position: ${fields.headingPosition};
        `,
    };

    const subHeading = {
        css: css`
            bottom: ${fields.subHeadingBottom};
            left: ${fields.subHeadingLeft}px;
            top: ${fields.subHeadingTop}px;
            position: ${fields.subHeadingPosition};
        `,
    };

    const bannerButtonStyle = {
        css: css`
            border-width: ${fields.btnBorderLine};
            margin-bottom: ${fields.btnMarginBottom}px;
            margin-left: ${fields.btnMarginLeft}%;
            margin-top: ${fields.btnMarginTop}%;
            margin-right: ${fields.btnMarginRight}%;
            min-width: ${fields.btnMinWidth}%;
            padding: ${fields.btnPadding}px;
        `,
    };

    return (
    <StyledWrapper {...customCssWrapper}>
        <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
            <StyledWrapper {...styles.imageWrapper}>
                {fields.image && (
                    <img src={fields.image} alt="" />
                )}
            </StyledWrapper>
            <StyledWrapper {...overlayParentStyles}>
                <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                    <StyledWrapper  className="banner-center-wrapper" {...styles.slideCenteringWrapperStyles} >
                        <Typography className="banner-heading" {...heading}>{parse(fields.heading, parserOptions)}</Typography>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography className="banner-subheading" {...subHeading}>{parse(fields.subheading, parserOptions)}</Typography>
                    </StyledWrapper>

                    {!fields.disableButton && (
                        <Button {...(styles.bannerButton, bannerButtonStyle)} variant={fields.variant} onClick={onClick}>
                            {fields.buttonLabel || title || url}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tooltip: "Image Size has to be 330 x 229",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.imageSize,
                editorTemplate: "TextField",
                displayName: "Recommended Image Size",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.overlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "top",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.imageVerticalAlignment,
                displayName: "Image Vertical Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.headingPosition,
                displayName: "Heading Layout  Style",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Static", value: "static" },
                    { displayName: "Absolute", value: "absolute" },
                    { displayName: "Relative", value: "relative" },
                ],
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.headingLeft,
                editorTemplate: "TextField",
                displayName: "Heading Left Spacing (px)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.headingTop,
                editorTemplate: "TextField",
                displayName: "Heading Top Spacing (px)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.headingBottom,
                editorTemplate: "TextField",
                displayName: "Heading Bottom Spacing (px)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.subHeadingPosition,
                displayName: "Sub Heading Layout Style",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Static", value: "static" },
                    { displayName: "Absolute", value: "absolute" },
                    { displayName: "Relative", value: "relative" },
                ],
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.subHeadingLeft,
                editorTemplate: "TextField",
                displayName: "Sub Heading Left Spacing (px)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.subHeadingTop,
                editorTemplate: "TextField",
                displayName: "Sub Heading Top Spacing (px)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.subHeadingBottom,
                editorTemplate: "TextField",
                displayName: "Sub Heading Bottom Spacing (px)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                fieldType: "Translatable",
                name: fields.btnMarginBottom,
                displayName: "Button Margin Bottom (%)",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.btnMarginLeft,
                displayName: "Button Margin Left (%)",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.btnMarginTop,
                displayName: "Button Margin Top (%)",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.btnMarginRight,
                displayName: "Button Margin Right (%)",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.btnMinWidth,
                displayName: "Button Min Width (%)",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.btnPadding,
                displayName: "Button Padding (px)",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.btnBorderLine,
                displayName: "Border Line Button Style",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Medium", value: "medium" },
                    { displayName: "Thin", value: "thin" },
                    { displayName: "Thick", value: "thick" },
                ],
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: "centerTextVertically",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
          },
        ],
    },
};

export default banner;
